import { http } from '@pendo/http';
import { BuildingBlockUtils } from '@pendo/services/BuildingBlocks';
import { APPS, DATA_COLLECTION_CONSENT_OPTIONS } from '@pendo/services/Constants';
import { convertToStaticLookasideConfigIfEligible, getDesignerOpenerUrl } from '@utils/designer-launch';
import get from 'lodash/get';
import { v4 as uuid } from 'uuid';
const { isWebBasedPlatform, isMobilePlatform } = BuildingBlockUtils;

const { EXTENSION_PLATFORM, EMAIL_PLATFORM } = APPS;

export const MINIMUM_AGENT_VERSIONS = {
    adoptGuideDisplayPriority: '2.66.0',
    guideOrdering: '2.8.0',
};

export const APP_PLATFORM_TYPES = {
    EXTENSION: 'extension',
    WEB: 'web',
    IOS: 'ios',
    ANDROID: 'android',
};

export const APP_PLATFORM_LIST = ['*', 'web', 'extension', 'mobile'];

export const EXPAND_APP_PLATFORM_LIST = [
    'expandAppIds("*")',
    'expandAppIds("web")',
    'expandAppIds("extension")',
    'expandAppIds("mobile")',
    'expandAppIds("ios")',
    'expandAppIds("android")',
];

export function getAppFromStep (step, guide) {
    const stepAppId = get(step, 'appIds[0]');

    return stepAppId || get(guide, 'appId', -323232);
}

export function getAppDisplayName (app) {
    if (app.trainingAttributes?.displayName) {
        return app.trainingAttributes.displayName;
    }

    return app.displayName || app.name;
}

export function isWebBasedApp (platform) {
    return isWebBasedPlatform(platform);
}

export function isMobileApp (platform = 'web') {
    return isMobilePlatform(platform);
}

export function isEmailPlatform (platform) {
    return platform === EMAIL_PLATFORM;
}

export function isExtensionApp (platform) {
    return platform === EXTENSION_PLATFORM;
}

// extension-type applications should be treated like web, as far as guide management is concerned
export function computeGuidePlatformType (platform) {
    if (isWebBasedApp(platform)) return APP_PLATFORM_TYPES.WEB;

    return platform;
}

export function getAppsForPlatforms (apps, platforms) {
    return apps.filter((app) => platforms.includes(app.platform));
}

export function getIntegratedApps (apps) {
    return apps.filter((app) => app.platform === APP_PLATFORM_TYPES.WEB || app.integrated);
}

export function getAppIconType (platform) {
    if (platform === 'web') return 'monitor';
    if (platform === 'extension') return 'puzzle-piece';
    if (platform === 'subscription') return 'layers';

    return platform;
}

export function expandPlatformAppId (appId, apps) {
    if (!EXPAND_APP_PLATFORM_LIST.includes(appId)) {
        return [];
    }

    switch (appId) {
        case 'expandAppIds("web")':
            return getAppsForPlatforms(apps, ['web']).map((app) => app.id);
        case 'expandAppIds("ios")':
            return getAppsForPlatforms(apps, ['ios']).map((app) => app.id);
        case 'expandAppIds("android")':
            return getAppsForPlatforms(apps, ['android']).map((app) => app.id);
        case 'expandAppIds("mobile")':
            return getAppsForPlatforms(apps, ['ios', 'android']).map((app) => app.id);
        case 'expandAppIds("extension")':
            return getAppsForPlatforms(apps, ['extension']).map((app) => app.id);
        default:
            return apps.map((app) => app.id);
    }
}

export function fetchExtensionPlatformTypes () {
    return http.get('/api/s/_SID_/daextension/schema/types').then((res) => res.data);
}

export async function meetsMinimumAgentVersion (appIds, version, subId = '_SID_') {
    const agentVersionsMap = await http
        .get(`/api/s/${subId}/environment/versioncheck`, {
            params: {
                v: version,
            },
        })
        .then((res) => res.data);
    const appMeetsMinimum = (appId) => get(agentVersionsMap[appId], 'production', false);

    return Array.isArray(appIds) ? appIds.every(appMeetsMinimum) : appMeetsMinimum(appIds);
}

export async function fetchLatestAppVersionForAppId (appId) {
    return http.get(`api/s/_SID_/application/${appId}/version/latest`).then((res) => res.data);
}

export function formatConsentOptions (platform) {
    // keep this order alphebetized by label
    return {
        ...(get(platform, 'htmlAttributes.length', false) && {
            htmlAttributes: {
                ...DATA_COLLECTION_CONSENT_OPTIONS.htmlAttributes,
                subConsentOptions: platform.htmlAttributes.reduce((subConsentOptions, htmlAttribute) => {
                    subConsentOptions[htmlAttribute] = {
                        label: htmlAttribute,
                        enabled: true,
                    };

                    return subConsentOptions;
                }, {}),
            },
        }),
        ...(get(platform, 'applicationFlags.pageBasedEventProperties', false) &&
            get(platform, 'eventPropertyConfigurations', []).some((ep) => ep.name === 'document_title') && {
            pageBasedEventProperties: {
                ...DATA_COLLECTION_CONSENT_OPTIONS.pageBasedEventProperties,
                subConsentOptions: platform.eventPropertyConfigurations.reduce((subConsentOptions, ep) => {
                    subConsentOptions[ep.name] = {
                        ...ep,
                        label: ep.name,
                        enabled: true,
                    };

                    return subConsentOptions;
                }, {}),
            },
        }),
        ...(get(platform, 'applicationFlags.siblingSelectors', false) && {
            siblingSelectors: {
                ...DATA_COLLECTION_CONSENT_OPTIONS.siblingSelectors,
            },
        }),
        // disableExtensionTextCapture default value is true
        ...(!get(platform, 'applicationFlags.disableExtensionTextCapture', true) && {
            disableExtensionTextCapture: {
                ...DATA_COLLECTION_CONSENT_OPTIONS.disableExtensionTextCapture,
            },
        }),
    };
}

export function startMobileDebugger (appId) {
    let config = {
        timestamp: Date.now(),
        host: window.designerLookaside || window.designerHost,
        type: 'mobileSDKDebug',
        idForApp: appId,
        disableFetchAgentAPIKey: true,
        keepUrlToken: true,
        target: 'mobile',
        designerId: uuid(),
    };
    config = convertToStaticLookasideConfigIfEligible(config);
    const launcherUrl = getDesignerOpenerUrl(window.location.href, config);
    window.history.pushState({}, '', launcherUrl);
    window.pendo.designerv2.launchInAppDesigner(config);
}

/** Single selective-URL struct → display string (app settings / catalog tooltip). */
export function extensionStructRegExpToDisplayString (regex) {
    if (!regex || typeof regex !== 'object') {
        return '';
    }
    const sub = regex.subDomainRegExp ?? '';
    const root = regex.rootDomain ?? '';
    const path = regex.pathRegExp ?? '';
    let str = `${sub}${root}${path}`;
    const frag = regex.fragmentRegExp;
    if (frag !== undefined && frag !== null && String(frag) !== '') {
        str += `#${frag}`;
    }

    return str;
}

export function extensionStructRegExpsToDisplayStrings (regexes) {
    if (!Array.isArray(regexes)) {
        return [];
    }

    return regexes.map(extensionStructRegExpToDisplayString);
}
